import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { User, Calendar as CalendarIcon, Hospital } from 'lucide-react';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { TreatmentRecord } from '../../../types/treatment';
import { TreatmentService } from '../../../services/treatmentService';
import { mockStaff } from '../../../mock/data';

interface BasicInfoSectionProps {
  record: Partial<TreatmentRecord>;
  onChange?: (updates: Partial<TreatmentRecord>) => void;
  mode?: 'view' | 'edit' | 'create';
}

export const BasicInfoSection: React.FC<BasicInfoSectionProps> = ({
  record,
  onChange,
  mode = 'view',
}) => {
  const [visitCount, setVisitCount] = useState<number>(1);
  const [editableVisitCount, setEditableVisitCount] = useState<number>(1);
  const isFirstVisit = record.isFirstVisit || false;
  
  useEffect(() => {
    // 来店回数を自動計算
    const updateVisitCount = async () => {
      if (record.customerId) {
        const count = await TreatmentService.getVisitCount(record.customerId);
        setVisitCount(count);
        setEditableVisitCount(count);
      }
    };
    
    updateVisitCount();
  }, [record.customerId]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <User className="h-5 w-5" />
          基本情報
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* 顧客情報 */}
          <div className="space-y-2">
            <h3 className="text-lg font-bold">{record.customerName || '未設定'}</h3>
            <div className="flex gap-2 items-center flex-wrap">
              {isFirstVisit ? (
                <Badge variant="default" className="bg-green-600">初回来店</Badge>
              ) : (
                mode === 'edit' || mode === 'create' ? (
                  <div className="flex items-center gap-1">
                    <Input
                      type="number"
                      value={editableVisitCount}
                      onChange={(e) => {
                        const newCount = parseInt(e.target.value) || 1;
                        setEditableVisitCount(newCount);
                        if (onChange) {
                          onChange({ visitCount: newCount });
                        }
                      }}
                      className="w-16 h-8"
                      min={1}
                    />
                    <span className="text-sm">回目</span>
                  </div>
                ) : (
                  <Badge variant="secondary">{visitCount}回目</Badge>
                )
              )}
              {record.tags?.includes('VIP') && (
                <Badge variant="secondary">VIP</Badge>
              )}
            </div>
          </div>

          {/* 日時情報 */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <CalendarIcon className="h-4 w-4 text-muted-foreground" />
              {mode === 'edit' || mode === 'create' ? (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-[200px] justify-start text-left font-normal",
                        !record.date && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {record.date ? format(new Date(record.date), 'yyyy年MM月dd日(E)', { locale: ja }) : '日付を選択'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={record.date ? new Date(record.date) : undefined}
                      onSelect={(newDate) => {
                        if (onChange && newDate) {
                          const dateOnly = new Date(newDate);
                          dateOnly.setHours(0, 0, 0, 0);
                          onChange({ date: dateOnly });
                        }
                      }}
                      locale={ja}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              ) : (
                <span className="text-sm">
                  {record.date ? format(new Date(record.date), 'yyyy年MM月dd日(E)', { locale: ja }) : '未設定'}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Hospital className="h-4 w-4 text-muted-foreground" />
              {mode === 'view' ? (
                <span className="text-sm">担当: {record.staffName || '未設定'}</span>
              ) : (
                <Select
                  value={record.staffId?.toString() || ''}
                  onValueChange={(value) => {
                    const selectedStaff = mockStaff.find(s => s.id === value);
                    if (onChange && selectedStaff) {
                      onChange({
                        staffId: parseInt(selectedStaff.id, 10),
                        staffName: selectedStaff.name,
                      });
                    }
                  }}
                >
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="スタッフを選択" />
                  </SelectTrigger>
                  <SelectContent>
                    {mockStaff.map((staff) => (
                      <SelectItem key={staff.id} value={staff.id}>
                        {staff.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
        </div>

        <Separator className="my-4" />

        {/* 前回来店情報 */}
        {!isFirstVisit && record.lastVisitDaysAgo !== undefined && (
          <div className="bg-blue-50 dark:bg-blue-950 p-3 rounded-md">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              前回来店から{record.lastVisitDaysAgo}日経過
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};